// eslint-disable-next-line no-unused-vars
import React, { memo, useState, useEffect, useRef } from 'react';
import { object, func } from 'prop-types';
import { mutate } from 'swr';
import { useContextSelector } from 'use-context-selector';
import omit from 'lodash/omit';
import { useTranslation } from 'react-i18next';

import download from 'Utils/download';
import OrderModel from 'Containers/OrderModel';
import OrderTooltip from 'Components/OrderTooltip/OrderTooltip';
import OrderProducts from 'Containers/Order/OrderProducts';
import OrderSummary from 'Containers/Order/OrderSummary';
import OrderInfo from 'Containers/Order/OrderInfo';
import OrderPageComponent from 'Components/Order/OrderPage';
import useFetch from 'Hooks/useFetch';
import { API_ORDERS, API_PROFORM, API_USER_CREDIT, API_USER_SOLD, API_ORDER_INVOICE } from 'Constants';
import { UserContext } from 'Contexts/UserContext';
import OrderInvoices from 'Components/Order/OrderInvoices';
import OrderAWBs from 'Components/Order/OrderAWBs';
import useToastNotifications from 'Hooks/useToastNotifications';
import useAsyncError from 'Hooks/useAsyncError';
import useOrderStatus from 'Hooks/useOrderStatus';
import useAnalytics from 'Hooks/useAnalytics';
import usePermissions from 'Hooks/usePermissions';

const OrderPage = memo(props => {
  const fetcher = useFetch();
  const toast = useToastNotifications();
  const analytics = useAnalytics();
  const { email } = useContextSelector(
    UserContext,
    data => ({
      email: data?.data?.email,
    })
  );
  const { isExternalReseller } = usePermissions();
  const { t } = useTranslation();
  const [order, setOrder] = useState(new OrderModel(props.order || {}));
  const [canPlaceOrder, setCanPlace] = useState(true);
  const [loadingOrderUpdate, setLoadingOrderUpdate] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [loadingCancelChanges, setLoadingCancelChanges] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState([]);
  const [orderProducts, setOrderProducts] = useState(props.order?.items || []);
  const [orderProductsWithChanges, setOrderProductsWithChanges] = useState([]);
  const [downloading, setDownloading] = useState(false);
  const [showAllInvoices, setShowAllInvoices] = useState(false);
  const [awbs, setAwbs] = useState([]);
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [unreservableProducts, setUnreservableProducts] = useState([]);
  const formRef = useRef(null);
  const awbsRef = useRef(null);
  const prevOrderIdRef = useRef(props.order?.id);
  const throwError = useAsyncError();
  const orderStatus = useOrderStatus(order);

  const stockReservation = order?.statusId === 2;

  useEffect(() => {
    if (props.order) {
      const orderIdChanged = prevOrderIdRef.current !== props.order.id;
      prevOrderIdRef.current = props.order.id;

      setOrder(prevOrder => new OrderModel({
        ...(prevOrder.getData() || {}),
        ...props.order
      }));

      if (orderIdChanged) {
        setOrderProducts([...(props.order.items || [])]);
        setOrderProductsWithChanges([]);
      }

      if (props.order?.awbsDetails) {
        setAwbs(props.order.awbsDetails.map(awb => ({
          ...awb,
          id: awb.awbNumber,
          number: awb.awbNumber,
          courier: awb.transporterName,
          productsCount: awb.productsCount,
          link: {
            number: awb.awbNumber,
            transporterName: awb.transporterName
          }
        })));
      }
    }
  }, [props.order]);

  useEffect(() => {
    if (orderStatus?.statusId !== 0) {
      setOrder(prevOrder => new OrderModel({
        ...(prevOrder.getData() || {}),
        ...orderStatus
      }));
    }
  }, [orderStatus]);

  useEffect(() => {
    const unsubscribe = order.subscribe(data => {
      setOrder(prevOrder => new OrderModel({
        ...(props.order || {}),
        ...prevOrder.getData(),
        ...data
      }));
    });

    return () => {
      unsubscribe();
    };
  }, [order]);

  useEffect(() => {
    if (!orderProducts?.length) {
      setCanPlace(false);
    } else {
      setCanPlace(() => orderProducts.filter(prod => prod.updated || prod.new)?.length === 0);

      const productsWithChanges = orderProducts.filter(product => product.updated || product.new || product.deleted);
      setOrderProductsWithChanges(productsWithChanges);
    }
  }, [orderProducts]);

  const toggleExpandRow = (rowId) => {
    setExpandedRows((prevExpandedRows) => {
      const newExpandedRows = new Set(prevExpandedRows);
      if (newExpandedRows.has(rowId)) {
        newExpandedRows.delete(rowId);
      } else {
        newExpandedRows.add(rowId);
      }
      return newExpandedRows;
    });
  };

  const expandRowProgrammatically = (rowId) => {
    setExpandedRows((prevExpandedRows) => new Set(prevExpandedRows).add(rowId));
    if (awbsRef.current) {
      awbsRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleOnGenerateInvoice = async () => {
    setDownloading(true);
    try {
      await download(`${ API_PROFORM }/${order.id}`);
    } catch (err) {
      toast.addNotification({
        type: 'error',
        title: 'Could not generate proforma'
      });
    } finally {
      setDownloading(false);
    }
  };

  const handleDownloadInvoiceAndWarranty = (isInvoice, invoiceId) => {
    download(`${API_ORDER_INVOICE}/${invoiceId}/${ isInvoice ? 'export?format=pdf' : 'warranty' }`);
  };

  const handleOnSubmit = async evt => {
    evt.preventDefault();
    const valid = formRef?.current?.checkValidity() || false;
    formRef?.current?.reportValidity();
    setErrors([]);
    if (
      !valid
      || !canPlaceOrder
    ) return;
    try {
      setLoading(true);
      const orderBody = {
        ...order.getData(),
        stockReservation: false
      };
      delete orderBody.items;
      if (orderBody.dropShipment) {
        orderBody.deliveryAddress = orderBody.deliveryAddressData;
      } else {
        orderBody.transportValue = 0;
      }
      delete orderBody.deliveryAddressData;
      if (orderBody.orderNotes?.length) {
        orderBody.comment = orderBody.orderNotes;
      }
      delete orderBody.orderNotes;
      const response = await fetcher(`${ API_ORDERS }/${ order.id }/confirm`, {
        method: 'POST',
        body: JSON.stringify(orderBody)
      });
      if (response) {
        setLoading(false);
        // get new sold / credit for user
        mutate([email, API_USER_SOLD]);
        mutate([email, API_USER_CREDIT]);
        props.updateOrder({
          ...props.order,
          ...response
        });
        setOrder(prevOrder => new OrderModel({
          ...(prevOrder.getData() || {}),
          ...(response || {}),
          stockReservation: false
        }));
      }

      analytics.track('reservation_place_order', { order_id: orderBody.id });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      setErrors([{ type: 'error', message: err?.info?.errors[0]?.message }]);
      throwError(err);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Add product to order
   * @param {object} product
   */
  const handleOnAddProduct = product => {
    const existingProductIdx = orderProducts.findIndex(prod => (
      !prod.appliedPromotions?.length
      && prod.id === product.id
    ));
    if (existingProductIdx !== -1) {
      toast.addNotification({
        type: 'warning',
        title: t('order.productAlreadyExists')
      });
      return false;
    }
    setOrderProducts(prevProducts => {
      prevProducts.push({
        ...product,
        quantity: product.minQuantity || 1,
        new: true
      });
      return [...prevProducts];
    });
    return true;
  };

  /**
   * Save product
   * @return {object} api response
   */
  const handleOnSaveProductUpdate = async () => {
    analytics.track('reservation_save_changes', { order_id: order.id });
    if (!orderProductsWithChanges.length) {
      toast.addNotification({
        title: t('order.orderNoChanges')
      });
      return;
    }

    setCanPlace(false);
    setLoadingOrderUpdate(true);
    const payloadItems = [];
    orderProductsWithChanges.forEach(product => {
      const payload = {
        minQuantity: product.minQuantity,
        quantity: product.deleted ? 0 : product.quantity,
        ...(product.new ? { id: product.id } : { orderDetailId: product.orderDetailId }),
      };
      if (Object.entries(product.promoItems || {}).length) {
        payload.appliedPromotions = [];
        payload.promoItems = Object
          .entries(product.promoItems)
          .reduce((acc, [promoKey, promoItems]) => {
            payload.appliedPromotions.push(promoKey);
            acc[promoKey] = promoItems.map(promoItem => {
              const p = {
                quantity: promoItem.quantity,
                minQuantity: promoItem.minQuantity
                // quantity: promoItem.quantity / promoItem.minQuantity
              };
              if (product.new) {
                p.id = promoItem.productId;
              } else {
                p.orderDetailId = promoItem.orderDetailId;
              }
              return p;
            });
            return acc;
          }, {});
      }
      payloadItems.push(payload);
    });
    try {
      const response = await fetcher(`${ API_ORDERS }/${ order.id }`, {
        method: 'PUT',
        body: JSON.stringify({
          items: payloadItems,
          comment: order?.orderNotes
        })
      });
      window.location.reload();
      toast.addNotification({
        title: t('order.addedNewProduct')
      });
      props.updateOrder({
        ...props.order,
        ...response
      });
      setOrder(prevOrder => new OrderModel({
        ...(prevOrder.getData() || {}),
        ...(response || {})
      }));
      setOrderProducts(prevProducts => {
        const updatedProducts = response.items.reduce((acc, item) => {
          const found = prevProducts.find(prod => prod.orderDetailId === item.orderDetailId || prod.id === item.id);
          if (found && !found.deleted) {
            acc.push(omit(item, ['new', 'updated', 'originalQuantity']));
          }
          return acc;
        }, []);

        return updatedProducts;
      });
      // eslint-disable-next-line consistent-return
    } catch (err) {
      const invalidStockProducts = [];
      const otherErrors = [];
      let hasAddedStockErrorToOtherErrors = false;
      if (err.info.status === 'error' && err.info.errors) {
        err.info.errors.forEach(error => {
          if (error.code === 'order.items.reservation_stock.invalid_reservation_stock') {
            invalidStockProducts.push(error.message_parameters['%code%']);
            if (!hasAddedStockErrorToOtherErrors) {
              otherErrors.push(error);
              hasAddedStockErrorToOtherErrors = true; // Ensure this only happens once
            }
          } else {
            otherErrors.push(error);
          }
        });
        if (invalidStockProducts.length) {
          setUnreservableProducts([...invalidStockProducts]);
        }
      }
      const messages = otherErrors.map(i => i?.message).join('\r\n');
      setErrors([{ type: 'error', message: messages }]);
      throwError(err);
    } finally {
      setLoadingOrderUpdate(false);
      setCanPlace(true);
    }
  };

  const handleProducts = async product => {
    const productId = (product.id || product.productId);
    const payload = {
      productId,
      quantity: product.quantity,
      orderDetailId: product.orderDetailId,
      minQuantity: product.minQuantity
    };
    const response = await fetcher(`${ API_ORDERS }/${ order.id }/items`, {
      method: 'PUT',
      body: JSON.stringify({
        items: [payload]
      })
    });

    return response;
  };

  /**
   * update product cb
   * @param {object} product
   */
  const handleOnUpdateProduct = async product => {
    const response = await handleProducts(product);

    if (product?.updated && response === null) {
      setOrderProducts(prevProducts => prevProducts);
      return;
    }

    setOrderProducts(prevProducts => {
      const existingProductIdx = prevProducts.findIndex(prod => {
        if (product.new) {
          return (
            (product.productId || product.id) === prod.id
            && !prod.appliedPromotions?.length
          );
        }
        if (product.appliedPromotions?.length) {
          return (
            product.appliedPromotions.at(0).promotionId === prod.appliedPromotions?.at(0)?.promotionId
            && product.orderDetailId === prod.orderDetailId
          );
        }
        return prod.orderDetailId === product.orderDetailId;
      });
      if (existingProductIdx !== -1) {
        const foundProduct = response.order.products.find(res => res.orderDetailId === product.orderDetailId);

        if (foundProduct) {
          const {
            price,
            promoPrice,
            totalPrice,
            totalPromoPrice,
          } = foundProduct;

          const updatedProduct = {
            ...product,
            price,
            promoPrice,
            totalPrice,
            totalPromoPrice,
          };

          prevProducts.splice(existingProductIdx, 1, updatedProduct);
          return [...prevProducts];
        }
      }
      return prevProducts;
    });
  };

  /**
   * Remove product from order
   * @param { object } product
   */
  const handleOnRemoveProduct = product => {
    if (!product.new) {
      setOrderProducts(prevProducts => prevProducts.map(prod => {
        const deletedId = product.productId ?? product.id;
        const currentId = prod.productId ?? prod.id;

        if (deletedId !== currentId) return prod;

        const deletedPromoId = product.appliedPromotions?.[0]?.promotionId ?? null;
        const currentPromoId = prod.appliedPromotions?.[0]?.promotionId ?? null;

        const deletedHasPromo = deletedPromoId !== null;
        const currentHasPromo = currentPromoId !== null;

        const shouldDelete = (deletedHasPromo && currentHasPromo && deletedPromoId === currentPromoId)
        || (!deletedHasPromo && !currentHasPromo);

        return shouldDelete ? { ...prod, deleted: true } : prod;
      })
      );
    } else {
      setOrderProducts(prevProducts => {
        prevProducts.splice(prevProducts.findIndex(p => p.id === product.id), 1);
        return [...prevProducts];
      });
    }
    return null;
  };

  const handleOnCancelRemoveProduct = product => {
    setOrderProducts(prevProducts => {
      const updatedPrevProducts = prevProducts.map(prod => {
        if ((product.productId || product.id) === prod.id) {
          return omit(prod, ['deleted']);
        }
        return prod;
      });
      return updatedPrevProducts;
    });
  };

  const handleCancelAllChanges = async () => {
    analytics.track('reservation_cancel_all_changes', { order_id: order.id });
    const withQuantityChanges = orderProductsWithChanges.filter(prod => prod.updated);

    if (withQuantityChanges.length) {
      // setCanPlace(false);
      // setLoadingCancelChanges(true);
      // TODO: temporary fix
      window.location.reload();
      // const payloadItems = [];
      // withQuantityChanges.forEach(product => {
      //   const payload = {
      //     minQuantity: product.minQuantity,
      //     quantity: product.originalQuantity,
      //     orderDetailId: product.orderDetailId,
      //   };

      //   payloadItems.push(payload);
      // });
      // try {
      //   const response = await fetcher(`${ API_ORDERS }/${ order.id }/items`, {
      //     method: 'PUT',
      //     body: JSON.stringify({
      //       items: payloadItems
      //     })
      //   });
      //   setOrderProducts(prevProducts => {
      //     const updatedProducts = response.order.products.reduce((acc, item) => {
      //       const found = prevProducts.find(prod => prod.orderDetailId === item.orderDetailId || prod.id === item.id);
      //       if (found) {
      //         acc.push(omit(item, ['updated', 'originalQuantity', 'maxQuantityAvailable']));
      //       }
      //       return acc;
      //     }, []);

      //     return updatedProducts;
      //   });
      // } catch (err) {
      //   setErrors({ type: 'error', message: err?.info?.errors[0]?.message });
      //   throwError(err);
      //   throw err;
      // } finally {
      //   setLoadingCancelChanges(false);
      //   setCanPlace(true);
      // }
    } else {
      setOrderProducts(prevProducts => {
        const updatedProducts = prevProducts.reduce((acc, item) => {
          if (!item.new) {
            acc.push(omit(item, ['deleted']));
          }

          return acc;
        }, []);

        return updatedProducts;
      });
    }
  };

  return (
    <OrderPageComponent
      stockReservation={ stockReservation }
      onGenerateInvoice={ handleOnGenerateInvoice }
      downloading={ downloading }
      onSubmit={ handleOnSubmit }
      loading={ loading }
      canPlaceOrder={ canPlaceOrder }
      errors={ errors }
      number={ props.order?.orderNumber }
      ds={ order?.dropShipment }
      formRef={ formRef }
      isSupplierOrder={ !!order?.supplierId }
    >
      <OrderTooltip order={ order } />
      <OrderProducts
        order={ order }
        editable={ stockReservation && !order.dropShipment }
        products={
          (orderProducts || []).length
            ? orderProducts
            : null
        }
        onUpdateProduct={ handleOnUpdateProduct }
        onAddProduct={ handleOnAddProduct }
        onSaveProductUpdate={ handleOnSaveProductUpdate }
        onRemoveProduct={ handleOnRemoveProduct }
        onCancelRemoveProduct={ handleOnCancelRemoveProduct }
        onCancelAllChanges={ handleCancelAllChanges }
        hasProductsWithChanges={ !!orderProductsWithChanges.length }
        loadingOrderUpdate={ loadingOrderUpdate }
        loadingCancelChanges={ loadingCancelChanges }
        expandRowProgrammatically={ expandRowProgrammatically }
        unreservableProducts={ unreservableProducts }
      />
      <div className="orderSummaryContainer">
        <OrderSummary
          order={ order }
          orderType={ stockReservation ? 'reservation' : 'ds' }
          totals={ order.getTotals() }
          canShowButtons={ false }
          canPlaceOrder={ false }
          displayVat={ !isExternalReseller }
        />
      </div>
      <OrderInfo
        editable={ stockReservation && !order.dropShipment }
        order={ order }
      />
      <OrderAWBs
        awbs={ awbs }
        awbsRef={ awbsRef }
        expandedRows={ expandedRows }
        toggleExpandRow={ toggleExpandRow }
        loading={ !order }
      />
      <OrderInvoices
        invoices={ props.order?.invoices || [] }
        isExternalReseller={ isExternalReseller }
        onDownloadInvoiceAndWarranty={ handleDownloadInvoiceAndWarranty }
        loading={ !order }
        showAllInvoices={ showAllInvoices }
        setShowAllInvoices={ setShowAllInvoices }
      />
    </OrderPageComponent>
  );
});

OrderPage.propTypes = {
  order: object,
  updateOrder: func,
};
OrderPage.displayName = 'OrderPageContainer';
export default OrderPage;
